/** Renders dotted patrol route lines for nearby enemies while the player is concealed (Trace passive). */
import Phaser from 'phaser';
import type { Enemy } from '../entities/Enemy';
import { ensureCircleTexture, CRISP_SCALE } from '../ui/CrispUtil';
import { distance, magnitude } from '../utils/Math';

const DOT_SPACING = 8;
const DOT_RADIUS = 1.5;
const LINE_ALPHA = 0.45;
const LINE_COLOR = 0xaaccff;
/** Only render trace for enemies within this range of the player. */
const TRACE_RANGE = 300;
/** Initial size of the Image pool. Grows automatically if more dots are needed. */
const INITIAL_POOL_SIZE = 64;

/**
 * Draws dotted patrol-route lookahead lines in world space for enemies the player
 * can observe while concealed. Activated by the Trace passive in the Specter tree.
 *
 * Uses a pool of pre-rendered circle Images instead of per-frame Graphics.fillCircle()
 * calls to avoid expensive earcut triangulation in Phaser's WebGL renderer.
 */
export class TraceRenderer {
  private scene: Phaser.Scene;
  private pool: Phaser.GameObjects.Image[] = [];
  private usedCount = 0;
  private enabled = false;
  private lookaheadSeconds = 3;
  private textureKey: string;
  private invScale: number;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.textureKey = ensureCircleTexture(scene, DOT_RADIUS * CRISP_SCALE);
    this.invScale = 1 / CRISP_SCALE;

    // Pre-allocate the initial pool
    for (let i = 0; i < INITIAL_POOL_SIZE; i++) {
      this.pool.push(this.createDot());
    }
  }

  /** Enable trace rendering with the given lookahead (from Trace passive effect). */
  enable(lookaheadSeconds: number): void {
    this.enabled = true;
    this.lookaheadSeconds = lookaheadSeconds;
  }

  /** Disable trace rendering. */
  disable(): void {
    this.enabled = false;
    this.hideAll();
  }

  /**
   * Draw patrol route dotted lines for all enemies with active patrol waypoints.
   * Only renders when the player is concealed and Trace is unlocked.
   */
  update(enemies: readonly Enemy[], playerX: number, playerY: number, isConcealed: boolean): void {
    this.usedCount = 0;

    if (!this.enabled || !isConcealed) {
      this.hideAll();
      return;
    }

    for (const enemy of enemies) {
      if (distance(enemy.sprite.x, enemy.sprite.y, playerX, playerY) > TRACE_RANGE) continue;

      const waypoints = enemy.ai.patrolWaypoints;
      if (waypoints.length < 2) continue;

      // Draw dots along the patrol path from current waypoint index
      const startIdx = enemy.ai.patrolWaypointIndex;
      let remaining = this.lookaheadSeconds; // approximate by waypoint count
      let prevX = enemy.sprite.x;
      let prevY = enemy.sprite.y;

      for (let i = 0; i < waypoints.length && remaining > 0; i++) {
        const wpIdx = (startIdx + i) % waypoints.length;
        const wp = waypoints[wpIdx];
        this.drawDottedLine(prevX, prevY, wp.x, wp.y);
        const segDist = distance(prevX, prevY, wp.x, wp.y);
        // Rough time estimate: ~60px/s patrol speed
        remaining -= segDist / 60;
        prevX = wp.x;
        prevY = wp.y;
      }
    }

    // Hide unused pool images
    for (let i = this.usedCount; i < this.pool.length; i++) {
      if (this.pool[i].visible) this.pool[i].setVisible(false);
    }
  }

  /** Draw a dotted line between two world positions. */
  private drawDottedLine(x1: number, y1: number, x2: number, y2: number): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = magnitude(dx, dy);
    if (len < 1) return;

    const nx = dx / len;
    const ny = dy / len;
    const dotCount = Math.floor(len / DOT_SPACING);

    for (let i = 0; i <= dotCount; i++) {
      const t = i * DOT_SPACING;
      const dot = this.getDot();
      dot.setPosition(x1 + nx * t, y1 + ny * t);
      dot.setVisible(true);
    }
  }

  /** Get a dot Image from the pool at the current usedCount index, growing the pool if needed. */
  private getDot(): Phaser.GameObjects.Image {
    if (this.usedCount >= this.pool.length) {
      this.pool.push(this.createDot());
    }
    return this.pool[this.usedCount++];
  }

  /** Create a new dot Image configured for trace rendering. */
  private createDot(): Phaser.GameObjects.Image {
    return this.scene.add.image(0, 0, this.textureKey)
      .setScale(this.invScale)
      .setDepth(1)
      .setTint(LINE_COLOR)
      .setAlpha(LINE_ALPHA)
      .setVisible(false);
  }

  /** Hide all dots in the pool. */
  private hideAll(): void {
    for (let i = 0; i < this.pool.length; i++) {
      if (this.pool[i].visible) this.pool[i].setVisible(false);
    }
  }

  /** Clean up all pooled Image objects. */
  destroy(): void {
    for (const dot of this.pool) {
      dot.destroy();
    }
    this.pool.length = 0;
  }
}
